/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a discount is applied */
  static discountUpdate = 'discount:update';
  /** @static @constant {string} Event triggered when changing collection filters */
  static FilterUpdate = 'filter:update';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The option value id
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {string} [resource.sku] - The SKU of the variant
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        errors,
        description,
      },
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/** Event class for facet filtering updates */
export class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams,
    };
  }

  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
  }
}


setTimeout(function () {
    const el = document.querySelector('.wcpa_price_summary');
    const inner_div = document.querySelector('.wcpa_total');

    if (!el) return;

    // Get original position before changing it
    const originalTop = el.getBoundingClientRect().top + window.scrollY;

    function updateSticky() {

        const scrollY = window.scrollY;
        const originalDivTop = originalTop - scrollY;

        // Show fixed version while original div is below viewport
        if (originalDivTop > window.innerHeight) {

            el.style.position = 'fixed';
            el.style.bottom = '0';
            el.style.right = '0';
            el.style.width = '32.5%';
            el.style.zIndex = '9999';
            el.style.padding = '0 15px';
            el.style.background = 'white';
            el.style.boxShadow = '6px 1px 11px gray';
            el.style.boxSizing = 'border-box';
            inner_div.style.padding = '15px';

        } else {

            // Original div is entering / visible
            el.style.position = '';
            el.style.bottom = '';
            el.style.right = '';
            el.style.width = '';
            el.style.zIndex = '';
            el.style.padding = '';
            el.style.background = '';
            el.style.boxShadow = '';
            el.style.boxSizing = '';
            inner_div.style.padding = '';
        }
    }

    // Run immediately on page load
    updateSticky();

    // Update while scrolling
    window.addEventListener('scroll', updateSticky);

}, 1000);



// Make color to dropdown Print Qty to red only ((Special Price)) in red color  STARTS
setTimeout(function () {

    document.querySelectorAll('select.wcpa_field').forEach(function (select) {

        // Avoid duplicate processing
        if (select.dataset.specialPriceStyled) return;
        select.dataset.specialPriceStyled = 'true';

        // Create visual text
        var display = document.createElement('div');
        display.className = 'wcpa-custom-display';

        function updateDisplay() {
            var option = select.options[select.selectedIndex];

            if (!option) return;

            var text = option.textContent;

            if (text.includes('(Special Price)')) {
                display.innerHTML = text.replace(
                    '(Special Price)',
                    '<span>(Special Price)</span>'
                );
            } else {
                display.textContent = text;
            }
        }

        updateDisplay();

        // Put visual display before select
        select.parentNode.insertBefore(display, select);

        // Make select transparent but STILL clickable
        select.style.position = 'absolute';
        select.style.inset = '0';
        select.style.width = '100%';
        select.style.height = '100%';
        select.style.opacity = '0';
        select.style.cursor = 'pointer';
        select.style.zIndex = '2';

        // Container needs to be positioned
        select.parentNode.style.position = 'relative';

        // Update visual text when ACO changes value
        select.addEventListener('change', updateDisplay);

    });

}, 1000);


// Make color to dropdown Print Qty to red only ((Special Price)) in red color  ENDS


// Hide default qty field when Print Qty are present on page and adjust with of add to cart and buy now buttons in a row STARTS
// setTimeout(() => {

//     const style_riz = document.createElement('style');
//     document.head.appendChild(style_riz);

//     const observer = new MutationObserver(() => {

//         const wcpaField = document.querySelector('select.wcpa_field');
//         const qtyWrappers = document.querySelectorAll('.quantity-selector-wrapper');

//         if (wcpaField) {

//             qtyWrappers.forEach(wrapper => {
//                 wrapper.style.display = 'none';
//             });

//             style_riz.innerHTML = `
//                 .quantity-selector-wrapper + span {
//                     flex-basis: 46% !important;
//                 }
//             `;

//         } else {

//             qtyWrappers.forEach(wrapper => {
//                 wrapper.style.display = '';
//             });

//             style_riz.innerHTML = `
//                 .quantity-selector-wrapper + span {
//                     flex-basis: 51% !important;
//                 }
//             `;
//         }
//     });

//     observer.observe(document.body, {
//         childList: true,
//         subtree: true
//     });

// }, 1000);



setTimeout(() => {

    const style_riz = document.createElement('style');
    document.head.appendChild(style_riz);


    // Change this to your Shopify product handle
    const targetProductHandle = '10-adjustable-telescopic-fabric-printing-frame';

    // Shopify product URL: /products/product-handle
    const currentProductHandle = window.location.pathname
        .split('/products/')[1]
        ?.split('/')[0];

    const isTargetProduct = currentProductHandle === targetProductHandle;

    const observer = new MutationObserver(() => {

        const wcpaField = document.querySelector('select.wcpa_field');
        const qtyWrappers = document.querySelectorAll('.quantity-selector-wrapper');

        if (isTargetProduct) {

            // ALWAYS show quantity for this product
            qtyWrappers.forEach(wrapper => {
                wrapper.style.display = '';
            });

            style_riz.innerHTML = `
                .quantity-selector-wrapper + span {
                    flex-basis: 46% !important;
                }
            `;

        } else if (wcpaField) {

            // Hide quantity when WCPA exists
            qtyWrappers.forEach(wrapper => {
                wrapper.style.display = 'none';
            });

            style_riz.innerHTML = `
                .quantity-selector-wrapper + span {
                    flex-basis: 46% !important;
                }
            `;

        } else {

            // Default behavior
            qtyWrappers.forEach(wrapper => {
                wrapper.style.display = '';
            });

            style_riz.innerHTML = `
                .quantity-selector-wrapper + span {
                    flex-basis: 51% !important;
                }
            `;
        }

    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

}, 1000);
// Hide default qty field when Print Qty are present on page and adjust with of add to cart and buy now buttons in a row ENDS



//  for calculate dynamic days for delivery and add text on dropdom select
document.addEventListener("DOMContentLoaded", function () {

    function updateMessage() {
        const selects = document.querySelectorAll('select.wcpa_field');
        const messageBox = document.getElementById('turnaround-message');

        if (!messageBox) return;

        selects.forEach(function (select) {

            const selectedText = select.options[select.selectedIndex].text;

            if (selectedText.includes("7-10 Business Days")) {

                let today = new Date();
                let daysToAdd = 7;
                let count = 0;
                let date = new Date(today);

                while (count < daysToAdd) {
                    date.setDate(date.getDate() + 1);

                    if (date.getDay() !== 0) { // skip Sunday
                        count++;
                    }
                }

                const options = { weekday: 'long', month: 'short', day: 'numeric' };
                const formattedDate = date.toLocaleDateString('en-US', options);

                messageBox.innerHTML = `
                    <strong>Order now</strong> and this item is estimated to be ready by 
                    <span style="color:#007bff;">${formattedDate}</span><br>
                    <em>* Please note that this is just an <strong> estimate </strong>. Actual production time may vary.</em>
                `;

            } else if (selectedText.includes("5-7 Business Days")) {
                let today = new Date();
                let daysToAdd = 5;
                let count = 0;
                let date = new Date(today);

                while (count < daysToAdd) {
                    date.setDate(date.getDate() + 1);

                    if (date.getDay() !== 0) { // skip Sunday
                        count++;
                    }
                }

                const options = { weekday: 'long', month: 'short', day: 'numeric' };
                const formattedDate = date.toLocaleDateString('en-US', options);

                messageBox.innerHTML = `
                    <strong>Order now</strong> and this item is estimated to be ready by 
                    <span style="color:#007bff;">${formattedDate}</span><br>
                    <em>* Please note that this is just an <strong> estimate </strong>. Actual production time may vary.</em>
                `;
            } 
            else {
                messageBox.innerHTML = '';
            }

        });
    }

    // Change event (delegated for ACO dynamic fields)
    document.addEventListener('change', function (e) {
        if (e.target.matches('select.wcpa_field')) {
            updateMessage();
        }
    });

    // Initial run
    // updateMessage();

    // Optional: handle ACO re-render
    setTimeout(updateMessage, 1500);

});





    // for show dropdown in one row
    document.addEventListener('DOMContentLoaded', function () {

    function updateFieldsLayout() {

        const suctionSelect = document.getElementById('field_select_2723263512');
        const grommetSelect = document.getElementById('field_select_2765887771');

        if (!suctionSelect || !grommetSelect) {
            return;
        }

        const suction = suctionSelect.value;
        const grommets = grommetSelect.value;


        // ==========================================
        // SUCTION CUPS
        // ==========================================

        const suctionField = document.getElementById('select_2723263512');

        if (suctionField) {

            const suctionRow = suctionField.closest('.wcpa_row');

            if (suction === 'Option - 1') {

                // Find the dynamically added Quantity field
                const quantityField = document.getElementById('number_7458159749');

                if (quantityField && suctionRow) {

                    // Parent row = 100%
                    suctionRow.style.width = '100%';
                    suctionRow.style.display = 'flex';
                    suctionRow.style.flexWrap = 'wrap';

                    // Suction Cups = 50%
                    suctionField.style.width = '50%';
                    suctionField.style.boxSizing = 'border-box';

                    // Quantity = 50%
                    const quantityFieldParent = quantityField.closest('.wcpa_field_wrap');

                    if (quantityFieldParent) {
                        quantityFieldParent.style.width = '50%';
                        quantityFieldParent.style.boxSizing = 'border-box';
                    }
                }

            } else {

                // Reset when No
                if (suctionRow) {
                    suctionRow.style.width = '';
                    suctionRow.style.display = '';
                    suctionRow.style.flexWrap = '';
                }

                suctionField.style.width = '';
            }
        }


        // ==========================================
        // GROMMETS / METAL HOLE
        // ==========================================

        const grommetField = document.getElementById('select_2765887771');

        if (grommetField) {

            const grommetRow = grommetField.closest('.wcpa_row');

            if (grommets === 'Option - 1') {

                const quantityField = document.getElementById('number_7011789713');

                if (quantityField && grommetRow) {

                    // Parent row = 100%
                    grommetRow.style.width = '100%';
                    grommetRow.style.display = 'flex';
                    grommetRow.style.flexWrap = 'wrap';

                    // Grommets = 50%
                    grommetField.style.width = '50%';
                    grommetField.style.boxSizing = 'border-box';

                    // Quantity = 50%
                    const quantityFieldParent = quantityField.closest('.wcpa_field_wrap');

                    if (quantityFieldParent) {
                        quantityFieldParent.style.width = '50%';
                        quantityFieldParent.style.boxSizing = 'border-box';
                    }
                }

            } else {

                // Reset when No
                if (grommetRow) {
                    grommetRow.style.width = '';
                    grommetRow.style.display = '';
                    grommetRow.style.flexWrap = '';
                }

                grommetField.style.width = '';
            }
        }
    }


    // ==========================================
    // SELECT CHANGE
    // ==========================================

    document.addEventListener('change', function (event) {

        if (
            event.target.id === 'field_select_2723263512' ||
            event.target.id === 'field_select_2765887771'
        ) {
            setTimeout(updateFieldsLayout, 100);
            setTimeout(updateFieldsLayout, 300);
            setTimeout(updateFieldsLayout, 600);
        }

    });


    // ==========================================
    // WATCH WCPA DOM CHANGES
    // ==========================================

    const section = document.querySelector('.wcpa_section_body');

    if (section) {

        const observer = new MutationObserver(function () {
            updateFieldsLayout();
        });

        observer.observe(section, {
            childList: true,
            subtree: true
        });
    }


    // Initial
    updateFieldsLayout();

});




// select image from product galary on select dropdown option
document.addEventListener('change', function (e) {
  if (e.target.id !== 'field_select_2708920301') return;

  var selected = e.target.value;

  // map ACO option value -> a unique fragment of the target image's filename
  var imageMap = {
    'Option - 4': 'WhatsApp-Image-2023-05-23-at-04.31.30_e83fdb7e-4b40-4065-bb95-9f02339291aa',
    // add more, e.g.
    'Option - 8': 'https://beebanner.com/cdn/shop/files/perforated_1_2_1.jpg?v=1790371401'
    // 'Option - 6': 'poster-3',
  };

  var fragment = imageMap[selected];
  if (!fragment) return;

  var thumbnails = document.querySelectorAll('.slideshow-controls__thumbnail');
  for (var i = 0; i < thumbnails.length; i++) {
    var img = thumbnails[i].querySelector('img');
    if (img && img.src.indexOf(fragment) !== -1) {
      thumbnails[i].click(); // fires the theme's built-in /select/N handler
      break;
    }
  }
});



document.addEventListener('change', function (e) {
  if (e.target.id !== 'field_select-1322691382') return;

  var selected2 = e.target.value;
  // map ACO option value -> a unique fragment of the target image's filename
  var imageMap2 = {
    'Option - 1': 'https://beebanner.com/cdn/shop/files/RSL-001.jpg?v=1784663517',
    // add more, e.g.
    'Option - 2': 'https://beebanner.com/cdn/shop/files/RSL-002.jpg?v=1784663517',

    '3': 'https://beebanner.com/cdn/shop/files/RSL-003.jpg?v=1784663517'
  };

  var fragment2 = imageMap2[selected2];
  if (!fragment2) return;

  var thumbnails2 = document.querySelectorAll('.slideshow-controls__thumbnail');
  for (var i = 0; i < thumbnails2.length; i++) {
    var img = thumbnails2[i].querySelector('img');
    if (img && img.src.indexOf(fragment2) !== -1) {
      thumbnails2[i].click(); // fires the theme's built-in /select/N handler
      break;
    }
  }
});


document.addEventListener('change', function (e) {
  if (e.target.id !== 'field_select-2888703760') return;

  var selected3 = e.target.value;
  // map ACO option value -> a unique fragment of the target image's filename
  var imageMap3 = {
    'Option - 1': 'https://beebanner.com/cdn/shop/files/RSW-001.jpg?v=1784663517',
    // add more, e.g.
    'Option - 2': 'https://beebanner.com/cdn/shop/files/RSW-002.jpg?v=1784663517',

    '3': 'https://beebanner.com/cdn/shop/files/RSW-003.jpg?v=1784663517'
  };

  var fragment3 = imageMap3[selected3];
  if (!fragment3) return;

  var thumbnails3 = document.querySelectorAll('.slideshow-controls__thumbnail');
  for (var i = 0; i < thumbnails3.length; i++) {
    var img = thumbnails3[i].querySelector('img');
    if (img && img.src.indexOf(fragment3) !== -1) {
      thumbnails3[i].click(); // fires the theme's built-in /select/N handler
      break;
    }
  }
});






document.addEventListener('change', function (e) {
  if (
    e.target.id !== 'field_select-8632071916' &&
    e.target.id !== 'field_select_6108723393' &&
    e.target.id !== 'field_select_6108719142' &&
    e.target.id !== 'field_select_6108726944'
  ) {
    return;
  }

  updateSliderImage();
});

function updateSliderImage() {
  var sizeField1 = document.getElementById('field_select-8632071916');
  var sizeField2 = document.getElementById('field_select_6108723393');
  var materialField = document.getElementById('field_select_6108719142');
  var shapeField = document.getElementById('field_select_6108726944');

  // Use the visible size field
  var sizeField = null;

  if (sizeField1 && sizeField1.offsetParent !== null) {
    sizeField = sizeField1;
  } else if (sizeField2 && sizeField2.offsetParent !== null) {
    sizeField = sizeField2;
  }

  var size = sizeField ? sizeField.value : '';
  var material = materialField ? materialField.value : '';
  var shape = shapeField ? shapeField.value : '';

  // Map Size + Material + Shape combinations to image URLs
  var imageMap = {
    // A Shape (Option - 1)
    '48x48|4mm|a-shape': 'https://beebanner.com/cdn/shop/files/a-shape.jpg?v=1792870392',
    '48x48|10mm|a-shape': 'https://beebanner.com/cdn/shop/files/forsale16.jpg?v=1792868509', 

    '96x48|4mm|a-shape': 'https://beebanner.com/cdn/shop/files/forsale7.jpg?v=1792868509',  
    '96x48|10mm|a-shape': 'https://beebanner.com/cdn/shop/files/forsale15.jpg?v=1792868509',  

    '48x96|4mm|a-shape': 'https://beebanner.com/cdn/shop/files/forsale8.jpg?v=1792868509',
    '48x96|10mm|a-shape': 'https://beebanner.com/cdn/shop/files/forsale6.jpg?v=1792868509', 

    // Straight Double Side (Option - 2)
    // Add the corresponding image URLs here.

    '48x48|4mm|double-side': 'https://beebanner.com/cdn/shop/files/forsale9.jpg?v=1792868509', 
    '48x48|10mm|double-side': 'https://beebanner.com/cdn/shop/files/forsale1.jpg?v=1792868510', 

    '96x48|4mm|double-side': 'https://beebanner.com/cdn/shop/files/forsale11.jpg?v=1792868509', 
    '96x48|10mm|double-side': 'https://beebanner.com/cdn/shop/files/forsale10.jpg?v=1792868509', 

    '48x96|4mm|double-side': 'https://beebanner.com/cdn/shop/files/forsale5.jpg?v=1792868509', 
    '48x96|10mm|double-side': 'https://beebanner.com/cdn/shop/files/forsale13.jpg?v=1792868509',  


  };

  var imageUrl = imageMap[size + '|' + material + '|' + shape];

  if (!imageUrl) return;

  // Find the matching product gallery thumbnail
  var thumbnails = document.querySelectorAll(
    '.slideshow-controls__thumbnail'
  );

  for (var i = 0; i < thumbnails.length; i++) {
    var img = thumbnails[i].querySelector('img');

    if (img && img.src.split('?')[0] === imageUrl.split('?')[0]) {
      thumbnails[i].click();
      return;
    }
  }
}

// Update when the page loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateSliderImage);
} else {
  updateSliderImage();
}



// document.addEventListener('DOMContentLoaded', () => {
//   const setWidth = (el, width) => {
//     el.style.setProperty('flex', `0 0 ${width}`, 'important');
//     el.style.setProperty('max-width', width, 'important');
//     el.style.setProperty('width', width, 'important');
//   };

//   function fixRows() {
//     document.querySelectorAll('.wcpa_row').forEach(row => {
//       const wraps = [...row.querySelectorAll('.wcpa_field_wrap')];
//       const find = text => wraps.find(w =>
//         w.querySelector('.wcpa_field_label')?.textContent.toLowerCase().includes(text)
//       );

//       const grommets = find('grommets');
//       if (!grommets) return;

//       const pocket = find('pocket');

//       row.style.display = 'flex';
//       row.style.flexWrap = 'wrap';

//       if (pocket) {
//         setWidth(grommets, '50%');
//         setWidth(pocket, '50%');
//       } else {
//         setWidth(grommets, '100%');
//       }
//     });
//   }

//   fixRows();

//   let timer;
//   new MutationObserver(() => {
//     clearTimeout(timer);
//     timer = setTimeout(fixRows, 100);
//   }).observe(document.body, { childList: true, subtree: true });
// });



// make full width Light field when quantity not present in product (Fabric Pop Up Media Wall (With Frame) 0
document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth() {
        const light = document.querySelector('#select_3928975851');
        const quantity = document.querySelector('#number_2945832293');
        const endcap1 = document.querySelector('#select_1684725599');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '33%', 'important');
            light.style.setProperty('flex', '0 0 33%', 'important');

            quantity.style.setProperty('width', '33%', 'important');
            quantity.style.setProperty('flex', '0 0 33%', 'important');

            endcap1.style.setProperty('width', '33%', 'important');
            endcap1.style.setProperty('flex', '0 0 33%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '67%', 'important');
            light.style.setProperty('flex', '0 0 67%', 'important');

            endcap1.style.setProperty('width', '33%', 'important');
            endcap1.style.setProperty('flex', '0 0 33%', 'important');

        }
    }

    updateLightWidth();

    const observer = new MutationObserver(function () {
        updateLightWidth();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});

document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth2() {
        const light = document.querySelector('#select_906869555');
        const quantity = document.querySelector('#number_635783297');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth2();

    const observer = new MutationObserver(function () {
        updateLightWidth2();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});


document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth3() {
        const light = document.querySelector('#wcpa-select-1678816370425');
        const quantity = document.querySelector('#wcpa-file-1678816412105');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth3();

    const observer = new MutationObserver(function () {
        updateLightWidth3();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});

document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth4() {
        const light = document.querySelector('#select-3660190444');
        const quantity = document.querySelector('#file_4954554781');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth4();

    const observer = new MutationObserver(function () {
        updateLightWidth4();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});



document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth5() {
        const light = document.querySelector('#wcpa-select-1678814654495');
        const quantity = document.querySelector('#wcpa-number-1678814679958');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth5();

    const observer = new MutationObserver(function () {
        updateLightWidth5();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});

document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth5() {
        const light = document.querySelector('#select-9184675285');
        const quantity = document.querySelector('#number-9204803292');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth5();

    const observer = new MutationObserver(function () {
        updateLightWidth5();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});


document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth5() {
        const light = document.querySelector('#select-9096778126');
        const quantity = document.querySelector('#number-9116906172');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth5();

    const observer = new MutationObserver(function () {
        updateLightWidth5();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});



document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth6() {
        const light = document.querySelector('#select_8804293347');
        const quantity = document.querySelector('#number_8805244348');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth6();

    const observer = new MutationObserver(function () {
        updateLightWidth6();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});


document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth6() {
        const light = document.querySelector('#select_222074998');
        const quantity = document.querySelector('#number_5237211');

        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '50%', 'important');
            light.style.setProperty('flex', '0 0 50%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '100%', 'important');
            light.style.setProperty('flex', '0 0 100%', 'important');
        }
    }

    updateLightWidth6();

    const observer = new MutationObserver(function () {
        updateLightWidth6();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});

document.addEventListener('DOMContentLoaded', function () {

    function updateLightWidth6() {
        const light = document.querySelector('#wcpa-select-1673966733452');
        const quantity = document.querySelector('#select-0816915511');
        // const finish = document.querySelector('#wcpa-select-1673967022119');
        const finish2 = document.querySelector('#select-1752271103');
        

      
        if (!light) return;

        const row = light.closest('.wcpa_row');

        if (!row) return;

        // Make the row a flex row
        row.style.display = 'flex';
        row.style.flexWrap = 'wrap';

        if (quantity) {
            // Quantity exists → 50 / 50
            light.style.setProperty('width', '33%', 'important');
            light.style.setProperty('flex', '0 0 33%', 'important');

            quantity.style.setProperty('width', '50%', 'important');
            quantity.style.setProperty('flex', '0 0 50%', 'important');
        } else {
            // Quantity doesn't exist → Light 100%
            light.style.setProperty('width', '33%', 'important');
            light.style.setProperty('flex', '0 0 33%', 'important');

            // finish.style.setProperty('width', '33%', 'important');
            // finish.style.setProperty('flex', '0 0 33%', 'important');  

            // finish2.style.setProperty('width', '33%', 'important');
            // finish2.style.setProperty('flex', '0 0 33%', 'important');
            
        }
    }

    updateLightWidth6();

    const observer = new MutationObserver(function () {
        updateLightWidth6();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

});

// setTimeout(function () {
//   document.getElementById('select_4902481406').removeAttribute('style');
// }, 2000);